<template>
  <main class="impact-assessment mt-12">
    <div class="mb-16">
      <h1
        class="font-Playfair md:text-[32px] text-[24px] md:leading-[42.66px] leading-[31.99px] mb-6"
      >
        SDG Alignment
      </h1>
      <p class="text-[16px] leading-6 font-Rubik text-greyText">
        At Kinfolk Venture Capital, we are looking for companies who are making a
        concerted effort to do their part in making progress towards the Sustainable
        Development Goals (SDGs).
        <button @click="learnMoreModal = true" class="underline font-medium font-Rubik text-[16px) text-black">
          Learn more
        </button>
      </p>
    </div>
    <div class="grid lg:grid-cols-2 grid-cols-1 gap-16 mt-10 pb-10 w-full">
      <div class="left flex flex-col justify-start items-center mb-2 xl:mb-0 w-[100%]">
        <div v-show="sdgIndex === 0">
          <div class="heading">
            <!-- <button>Back</button> -->
            <div class="flex items-center mb-4">
              <span class="mr-[10px]">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    r="9"
                    transform="matrix(1 0 0 -1 12 12)"
                    stroke="#3D3D3D"
                    stroke-width="6"
                    stroke-dasharray="3.5 3.5"
                  />
                </svg>
              </span>
              <h3 class="font-medium text-[18px] leading-[32px] ">Plan your progress</h3>
            </div>
            <p class="text-greyText text-[16px] leading-6">
              Find specific SDGs or iKPIs ("Impact Key Performance Indicators") that your
              business can positively influence.
            </p>
            <br />
          </div>

          <div class="sdg-cards grid grid-cols-3 gap-3">
            <div v-for="(goal, goalIndex) in goals" :key="goalIndex" @click="checkSDG(goal.goal)" class="cursor-pointer">
              <img :src="goal.img" :alt="goal.goal" class="w-full h-full" />
            </div>
            <a href="https://sdgs.un.org/goals" class="cursor-pointer flex items-center justify-center text-greyText">
              <svg class="w-[50px] h-[50px] opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Zm4-9H13V8a1,1,0,0,0-2,0v3H8a1,1,0,0,0,0,2h3v3a1,1,0,0,0,2,0V13h3a1,1,0,0,0,0-2Z"/>
              </svg>
            </a>
          </div>
        </div>
        <div v-if="sdgIndex > 0">
          <div class="heading flex items-center">
            <button @click="sdgIndex = 0" class="flex items-center py-[7px] px-3 border-[2px] border-faintGrey rounded-[8px] mr-[22px]">
              <span class="mr-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.33398 9.99967C3.33398 9.77866 3.42178 9.5667 3.57806 9.41042L8.57806 4.41042C8.9035 4.08498 9.43114 4.08498 9.75657 4.41042C10.082 4.73586 10.082 5.26349 9.75657 5.58893L6.17916 9.16634L15.834 9.16634C16.2942 9.16634 16.6673 9.53944 16.6673 9.99967C16.6673 10.4599 16.2942 10.833 15.834 10.833L6.17916 10.833L9.75657 14.4104C10.082 14.7359 10.082 15.2635 9.75657 15.5889C9.43114 15.9144 8.9035 15.9144 8.57806 15.5889L3.57806 10.5889C3.42178 10.4326 3.33398 10.2207 3.33398 9.99967Z"
                    fill="#0D0D0D"
                  />
                </svg>
              </span>
              Back
            </button>
            <div class="flex items-center">
              <span class="mr-[10px]">
                <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="6" height="5" rx="1" fill="black"/>
                  <rect x="7" width="6" height="5" rx="1" fill="black"/>
                  <rect x="14" width="6" height="5" rx="1" fill="black"/>
                  <rect y="7" width="6" height="5" rx="1" fill="black"/>
                  <rect x="7" y="7" width="6" height="5" rx="1" fill="black"/>
                  <rect x="14" y="7" width="6" height="5" rx="1" fill="black"/>
                  <rect y="14" width="6" height="5" rx="1" fill="black"/>
                  <rect x="7" y="14" width="6" height="5" rx="1" fill="black"/>
                  <rect x="14" y="14" width="6" height="5" rx="1" fill="black"/>
                </svg>
              </span>
              <h3 class="font-medium text-[18px] leading-[32px] flex items-center justify-between">
                <span class="block mr-2">{{sdgIndex}} </span>
                {{goals[sdgIndex-1].goal}}</h3>
            </div>
          </div>

          <div class="sdg-indices">
            <div v-for="(kpi, kpiIndex) in goals[sdgIndex-1].kpis" :key="kpiIndex" class="relative border-b-[2px] border-faintGrey py-[32px]">
              <div class="flex">
                <span class="mr-[10px] text-greyText font-medium">{{sdgIndex}}.{{kpi.id}}</span>
                <div>
                  <p class="font-medium text-[16px] leading-[24px] mb-4">{{kpi.title}}</p>
                  <div v-for="(s, sIndex) in kpi.kpi" :key="sIndex" class="flex justify-between py-3">
                    <p class="text-[16px] leading-[24px] text-greyText mr-[12px]">{{s}}</p>
                    <button
                      v-if="checkSelected(s)"
                      @click="removeSDG(s, goals[sdgIndex-1])"
                      class="h-[48px] py-2 px-[14px] rounded-[6px] font-medium text-[16px] leading-[24px] text-center bg-black max-w-[78px] relative w-full flex items-center justify-center"
                    >
                      <span class="text-white">
                        <img src="../../assets/white-tick.png" alt="white-tick">
                      </span>
                    </button>
                    <button
                      v-else
                      @click="getSDG(s, goals[sdgIndex-1])"
                      class="h-[48px] py-2 px-[14px] rounded-[6px] font-medium text-[16px] leading-[24px] text-center border-[2px] bg-white max-w-[78px] border-greyText relative w-full flex items-center justify-center"
                    >
                      <span class="text-black">Select</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="right px-4 w-full mb-2 xl:mb-0 relative"
      >
        <div class="sticky top-[100px] max-h-[calc(100vh-130px)] overflow-y-auto">
          <div class="heading">
            <!-- <button>Back</button> -->
            <div class="flex items-center mb-4">
              <span class="mr-[10px]">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M7.19967 0.104651C6.32753 0.127151 5.60771 0.830003 5.55843 1.69593H5.11218C3.96735 1.69593 3.02344 2.63486 3.02344 3.77969V21.3683C3.02344 22.5132 3.96735 23.4521 5.11218 23.4521H18.7187C19.8635 23.4521 20.8062 22.5132 20.8062 21.3683V3.77969C20.8062 2.63486 19.8635 1.69593 18.7187 1.69593H18.2724C18.2222 0.815923 17.4788 0.104651 16.5874 0.104651C15.696 0.104651 14.9538 0.81574 14.9037 1.69593H13.5999C13.5497 0.815923 12.8063 0.104651 11.9149 0.104651C11.0234 0.104651 10.2813 0.81574 10.2311 1.69593H8.92734C8.87716 0.815923 8.13379 0.104651 7.24235 0.104651C7.22789 0.104651 7.21414 0.104294 7.19985 0.104651H7.19967ZM7.22092 1.38466C7.22825 1.3843 7.23485 1.38466 7.24217 1.38466C7.47843 1.38466 7.64843 1.55466 7.64843 1.79091V2.87718C7.64843 3.11343 7.47842 3.28343 7.24217 3.28343C7.00592 3.28343 6.83592 3.11343 6.83592 2.87718V1.79091C6.83592 1.56198 6.9961 1.39483 7.22093 1.38466H7.22092ZM11.8934 1.38466C11.9007 1.3843 11.9073 1.38466 11.9147 1.38466C12.1509 1.38466 12.3209 1.55466 12.3209 1.79091V2.87718C12.3209 3.11343 12.1509 3.28343 11.9147 3.28343C11.6784 3.28343 11.5084 3.11343 11.5084 2.87718V1.79091C11.5084 1.56198 11.6686 1.39483 11.8934 1.38466H11.8934ZM16.5659 1.38466C16.5732 1.3843 16.5798 1.38466 16.5871 1.38466C16.8234 1.38466 16.9934 1.55466 16.9934 1.79091V2.87718C16.9934 3.11343 16.8234 3.28343 16.5871 3.28343C16.3509 3.28343 16.1809 3.11343 16.1809 2.87718V1.79091C16.1809 1.56198 16.3411 1.39483 16.5659 1.38466ZM5.11211 2.97219H5.55837C5.60855 3.85237 6.35069 4.56346 7.24212 4.56346C8.13356 4.56346 8.87697 3.85237 8.92711 2.97219H10.2309C10.2811 3.85237 11.0232 4.56346 11.9146 4.56346C12.8061 4.56346 13.5495 3.85237 13.5996 2.97219H14.9034C14.9536 3.85237 15.6957 4.56346 16.5872 4.56346C17.4786 4.56346 18.222 3.85237 18.2722 2.97219H18.7184C19.1763 2.97219 19.5259 3.32183 19.5259 3.77969V21.3683C19.5259 21.8262 19.1763 22.1721 18.7184 22.1721H5.11195C4.65407 22.1721 4.30445 21.8262 4.30445 21.3683V3.77969C4.30445 3.32181 4.65409 2.97219 5.11195 2.97219H5.11211ZM9.8646 5.9322C9.69478 5.92792 9.53013 5.99131 9.40709 6.10845H9.40584L7.75335 7.67719L7.14333 7.0397C6.9003 6.78488 6.49688 6.77434 6.24084 7.01613C5.98584 7.25917 5.9753 7.6624 6.21709 7.91863L7.26207 9.02364C7.37886 9.14721 7.54029 9.219 7.71029 9.22346C7.88029 9.22793 8.04511 9.16453 8.16835 9.04721L10.2896 7.0397C10.4135 6.92292 10.4857 6.76131 10.4901 6.59095C10.4946 6.42059 10.431 6.25559 10.3133 6.13221C10.1964 6.00863 10.0348 5.93667 9.86459 5.9322L9.8646 5.9322ZM11.8684 6.93595C11.6978 6.93542 11.5339 7.00292 11.4134 7.12346C11.2926 7.24417 11.2251 7.40793 11.2259 7.57846C11.2266 7.74828 11.2946 7.91078 11.4151 8.03042C11.5355 8.14989 11.6985 8.21667 11.8684 8.21596H17.1498C17.5012 8.21471 17.7858 7.93007 17.7872 7.57847C17.788 7.40883 17.7212 7.24579 17.6015 7.12525C17.4821 7.00472 17.3196 6.93668 17.1497 6.93597L11.8684 6.93595ZM9.86583 11.5335C9.69583 11.5283 9.53083 11.5908 9.40709 11.7072H9.40584L7.75335 13.276L7.14333 12.6385C6.9003 12.3836 6.49688 12.3731 6.24084 12.6149C5.98584 12.8579 5.9753 13.2612 6.21709 13.5174L7.26207 14.6224C7.37886 14.746 7.54029 14.8178 7.71029 14.8222C7.88029 14.8267 8.04511 14.7633 8.16835 14.646L10.2896 12.6385C10.5444 12.3954 10.5551 11.9922 10.3133 11.736C10.1971 11.6117 10.036 11.5388 9.86584 11.5335L9.86583 11.5335ZM11.8684 12.5385C11.6984 12.5379 11.5351 12.6049 11.4146 12.7247C11.2941 12.8445 11.2262 13.0074 11.2259 13.1772C11.2251 13.3479 11.2926 13.5117 11.4134 13.6324C11.5339 13.7529 11.6978 13.8204 11.8684 13.8197H17.1498C17.3196 13.8192 17.4821 13.7511 17.6015 13.6306C17.7212 13.5101 17.788 13.347 17.7873 13.1772C17.7865 12.8252 17.5017 12.5399 17.1498 12.5384L11.8684 12.5385ZM9.8646 17.1337C9.69459 17.1297 9.52995 17.1937 9.40709 17.3112H9.40584L7.75335 18.8787L7.14333 18.2362C6.9003 17.9813 6.49688 17.9708 6.24084 18.2126C6.11691 18.3296 6.04458 18.491 6.04012 18.6613C6.03566 18.8317 6.09941 18.9967 6.21709 19.1201L7.26207 20.2214C7.3785 20.3455 7.5394 20.418 7.7094 20.4231C7.87958 20.4283 8.04458 20.3656 8.16833 20.2489L10.2896 18.2364C10.5437 17.9935 10.5542 17.591 10.3133 17.3351C10.1965 17.2108 10.0349 17.1385 9.86457 17.1339L9.8646 17.1337ZM11.8684 18.1374C11.6978 18.1369 11.5339 18.2044 11.4134 18.3249C11.2926 18.4456 11.2251 18.6094 11.2259 18.7799C11.2266 18.9498 11.2946 19.1123 11.4151 19.2319C11.5355 19.3514 11.6985 19.4181 11.8684 19.4174H17.1498C17.5012 19.4162 17.7858 19.1315 17.7872 18.7799C17.788 18.6103 17.7212 18.4473 17.6015 18.3267C17.4821 18.2062 17.3196 18.1382 17.1497 18.1374L11.8684 18.1374Z"
                    fill="#3D3D3D"
                  />
                </svg>
              </span>
              <h3 class="font-medium text-[18px] leading-[32px] ">
                Your selected Progress
                <span
                  class="ml-[10px] rounded-full bg-faintGrey text-black font-bold text-[18px] px-[8px] py-[4px] leading-[14px] text-center w-[14px] h-[14px]"
                  >{{selectedSDGs.length}}</span
                >
              </h3>
            </div>
            <p class="text-greyText text-[16px] leading-6">
              Your chosen iKPIs are displayed below. Click on the save button to share
              with the Kinfolk team.
            </p>
            <br />
          </div>
          <div class="sdg-cards grid grid-cols-4 gap-3 mb-6">
            <div v-for="(goal, goalIndex) in selectedGoals" :key="goalIndex" class="cursor-pointer">
              <img :src="goal.img" :alt="goal.goal" class="w-full" />
            </div>
          </div>
          <template v-if="selectedSDGs.length > 0">
            <div v-for="(sdg, index) in  selectedKpiId" :key="index" class="flex items-center relative border-2 border-faintGrey rounded-[6px] p-[20px] text-[16px] leading-[24px] text-greyText mb-3">
              <p class="max-w-[90%]">{{sdg.kpi}}</p>
              <button @click="spliceSDG(sdg, sdg.goal)" class="absolute right-[20px]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.4099 12.0002L17.7099 7.71019C17.8982 7.52188 18.004 7.26649 18.004 7.00019C18.004 6.73388 17.8982 6.47849 17.7099 6.29019C17.5216 6.10188 17.2662 5.99609 16.9999 5.99609C16.7336 5.99609 16.4782 6.10188 16.2899 6.29019L11.9999 10.5902L7.70994 6.29019C7.52164 6.10188 7.26624 5.99609 6.99994 5.99609C6.73364 5.99609 6.47824 6.10188 6.28994 6.29019C6.10164 6.47849 5.99585 6.73388 5.99585 7.00019C5.99585 7.26649 6.10164 7.52188 6.28994 7.71019L10.5899 12.0002L6.28994 16.2902C6.19621 16.3831 6.12182 16.4937 6.07105 16.6156C6.02028 16.7375 5.99414 16.8682 5.99414 17.0002C5.99414 17.1322 6.02028 17.2629 6.07105 17.3848C6.12182 17.5066 6.19621 17.6172 6.28994 17.7102C6.3829 17.8039 6.4935 17.8783 6.61536 17.9291C6.73722 17.9798 6.86793 18.006 6.99994 18.006C7.13195 18.006 7.26266 17.9798 7.38452 17.9291C7.50638 17.8783 7.61698 17.8039 7.70994 17.7102L11.9999 13.4102L16.2899 17.7102C16.3829 17.8039 16.4935 17.8783 16.6154 17.9291C16.7372 17.9798 16.8679 18.006 16.9999 18.006C17.132 18.006 17.2627 17.9798 17.3845 17.9291C17.5064 17.8783 17.617 17.8039 17.7099 17.7102C17.8037 17.6172 17.8781 17.5066 17.9288 17.3848C17.9796 17.2629 18.0057 17.1322 18.0057 17.0002C18.0057 16.8682 17.9796 16.7375 17.9288 16.6156C17.8781 16.4937 17.8037 16.3831 17.7099 16.2902L13.4099 12.0002Z" fill="#F45252"/>
                </svg>
              </button>
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col items-center justify-center my-[90px]">
              <span class="block mb-6">
                <svg width="77" height="79" viewBox="0 0 77 79" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M61.9051 28.1081V8.68469C61.9051 6.47661 61.0281 4.35969 59.4673 2.79821C57.9058 1.23739 55.7889 0.360369 53.5808 0.360369H25.4176C24.221 0.357892 23.038 0.613698 21.9492 1.10981C21.0449 1.53036 20.2187 2.10266 19.5077 2.80195L3.21896 18.9511C2.50792 19.6529 1.93438 20.481 1.52619 21.3926C1.03007 22.4815 0.774888 23.6644 0.777362 24.8611V69.73C0.777362 71.9381 1.65438 74.055 3.2152 75.6165C4.77664 77.1773 6.8936 78.0543 9.10168 78.0543H53.4981C55.7055 78.0543 57.8231 77.1773 59.3839 75.6165C60.9454 74.055 61.8224 71.9381 61.8224 69.73V67.8156C62.9726 67.2618 64.0689 66.6028 65.0963 65.8453L71.7558 72.5047C72.8428 73.5918 74.6061 73.5886 75.6893 72.4978C76.767 71.4124 76.7639 69.6598 75.6823 68.5782L69.0089 61.9048C71.5768 58.5819 72.9809 54.5065 73.0044 50.3066C73.0044 45.1553 70.9581 40.2146 67.3155 36.5721C63.6729 32.9295 58.7323 30.8831 53.581 30.8831C48.4298 30.8831 43.4891 32.9295 39.8465 36.5721C36.2039 40.2146 34.1576 45.1553 34.1576 50.3066C34.1576 55.4578 36.2039 60.3985 39.8465 64.0411C43.4891 67.6836 48.4298 69.73 53.581 69.73C54.5101 69.7263 55.4379 69.6519 56.3558 69.5083V69.73C56.3558 70.4658 56.0634 71.1719 55.5432 71.6921C55.0229 72.2124 54.3168 72.5048 53.581 72.5048H9.18461C8.44881 72.5048 7.74271 72.2124 7.22245 71.6921C6.70219 71.1719 6.40984 70.4658 6.40984 69.73V25.3336H17.5089C19.717 25.3336 21.8339 24.4566 23.3954 22.8958C24.9562 21.3343 25.8333 19.2174 25.8333 17.0093V5.91017H53.581C54.3168 5.91017 55.0229 6.20252 55.5432 6.72278C56.0634 7.24305 56.3558 7.94914 56.3558 8.68495V28.1084L61.9051 28.1081ZM39.7069 50.3063C39.7069 46.6266 41.1686 43.0981 43.7706 40.4961C46.3726 37.8942 49.9011 36.4324 53.5808 36.4324C57.2605 36.4324 60.789 37.8942 63.391 40.4961C65.9929 43.0981 67.4547 46.6266 67.4547 50.3063C67.4547 53.986 65.9929 57.5145 63.391 60.1165C60.789 62.7184 57.2605 64.1802 53.5808 64.1802C49.9011 64.1802 46.3726 62.7185 43.7706 60.1165C41.1687 57.5145 39.7069 53.986 39.7069 50.3063ZM17.5087 19.7838H10.3222L20.2835 9.82251V17.009C20.2835 17.7448 19.9912 18.4509 19.4709 18.9712C18.9506 19.4914 18.2445 19.7838 17.5087 19.7838Z" fill="#828282"/>
                </svg>
              </span>
              <p class="text-[14px] text-greyText leading-[20px] text-center">No SDG has been selected yet</p>
            </div>
          </template>
          <button class="bg-black py-[14px] w-full rounded-[6px] text-[16px] leading-[24px] font-medium text-white text-center disabled:opacity-50" :disabled="selectedSDGs.length === 0">Save</button>
        </div>
      </div>
    </div>
    <Modal :modalActive="learnMoreModal === true">
      <div class="bg-white w-[399px] md:w-[565px] rounded-[6px] p-6 relative">
        <button @click.prevent="learnMoreModal = false" class="block absolute right-[30px]">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L7 5.58579L12.2929 0.292893C12.6834 -0.0976311 13.3166 -0.0976311 13.7071 0.292893C14.0976 0.683417 14.0976 1.31658 13.7071 1.70711L8.41421 7L13.7071 12.2929C14.0976 12.6834 14.0976 13.3166 13.7071 13.7071C13.3166 14.0976 12.6834 14.0976 12.2929 13.7071L7 8.41421L1.70711 13.7071C1.31658 14.0976 0.683417 14.0976 0.292893 13.7071C-0.0976311 13.3166 -0.0976311 12.6834 0.292893 12.2929L5.58579 7L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z" fill="#3D3D3D"/>
          </svg>
        </button>
        <div class="flex flex-col items-start justify-center">
          <h3 class="font-medium font-Rubik md:text-[18px] text-[16px] leading-[28px] text-black mb-[12px] text-left md:w-[auto] w-full">
            What is our Impact Assessment about?
          </h3>
          <div class="block mb-4 border-[1px] border-b-faintGrey w-full"></div>
          <p class="font-Rubik text-[16px] leading-[24px] text-greyText max-w-[95%] text-left mb-4">At Kinfolk Venture Capital, we are looking for companies who are making a concerted effort to do their part in making progress towards the Sustainable Development Goals, and has (at a minimum);</p>
          <ol class="list-decimal ml-[20px]">
            <li class="font-Rubik md:text-[16px] text-[14px] leading-[24px] text-greyText font-normal">A mission aligned with one or more SDGs.</li>
            <li class="font-Rubik md:text-[16px] text-[14px] leading-[24px] text-greyText font-normal">A product or offering that positively influences Key Performance Indicators towards the SDGs, AKA "Impact Key Performance Indicators" (iKPIs).</li>
            <li class="font-Rubik md:text-[16px] text-[14px] leading-[24px] text-greyText font-normal">A public webpage that tracks progress on their iKPIs on a regular basis.</li>
          </ol>
        </div>
      </div>
    </Modal>
  </main>
</template>

<script>
import { reactive, ref } from 'vue'
import goals from '@/utils/sdgs'
import Modal from '../../components/modal/Modal.vue'

export default {
  setup () {
    const sdgIndex = ref(0)
    const selectedProgress = ref(null)
    const selectedSDGs = reactive([])
    const activeIndex = ref(null)
    const selectedGoals = ref([])
    const selectedGoalsId = reactive([])
    const selectedKpiId = reactive([])
    const selectedKpi = ref([])
    const learnMoreModal = ref(false)

    const checkSDG = (sdg) => {
      switch (sdg) {
        case 'No Poverty':
          sdgIndex.value = 1
          break
        case 'Zero Hunger':
          sdgIndex.value = 2
          break
        case 'Good Health and Well-being':
          sdgIndex.value = 3
          break
        case 'Quality Education':
          sdgIndex.value = 4
          break
        case 'Gender Equality':
          sdgIndex.value = 5
          break
        case 'Clean Water and Sanitation':
          sdgIndex.value = 6
          break
        case 'Affordable and Clean Energy':
          sdgIndex.value = 7
          break
        case 'Decent Work and Economic Growth':
          sdgIndex.value = 8
          break
        case 'Industry, Innovation, and Infrastructure':
          sdgIndex.value = 9
          break
        case 'Reduced Inequalities':
          sdgIndex.value = 10
          break
        case 'Sustainable Cities and Communities':
          sdgIndex.value = 11
          break
        case 'Responsible Consumption and Production':
          sdgIndex.value = 12
          break
        case 'Climate Action':
          sdgIndex.value = 13
          break
        case 'Life Below Water':
          sdgIndex.value = 14
          break
        case 'Life On Land':
          sdgIndex.value = 15
          break
        case 'Peace, Justice, and Strong Institutions':
          sdgIndex.value = 16
          break
        case 'Partnerships for the Goals':
          sdgIndex.value = 17
          break
        default:
          sdgIndex.value = 0
      }
    }
    const getSDG = (sdgIdx, goals) => {
      selectedGoalsId.push(goals)
      console.log(selectedGoalsId)
      selectedKpiId.push({
        id: goals.id,
        goal: goals.goal,
        kpi: sdgIdx
      })
      selectedGoals.value = [...new Set(selectedGoalsId.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))
      if (!(selectedSDGs.includes(sdgIdx))) {
        selectedSDGs.push(sdgIdx)
      }
    }
    const removeSDG = (sdgIdx, goals) => {
      selectedSDGs.pop(sdgIdx)
      selectedGoalsId.pop(goals)
      selectedKpiId.pop({
        id: goals.id,
        goal: goals.goal,
        kpi: sdgIdx
      })
      selectedGoals.value = [...new Set(selectedGoalsId.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))
    }
    const spliceSDG = (sdg, sdggoal) => {
      selectedSDGs.pop(sdg.kpi)
      selectedKpiId.pop(sdg)
      const index = selectedGoalsId.findIndex(p => p.goal === sdggoal)
      selectedGoalsId.splice(index, 1)
      selectedGoals.value = [...new Set(selectedGoalsId.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))
    }
    const checkSelected = (value) => {
      return selectedSDGs.includes(value)
    }
    return {
      sdgIndex,
      selectedProgress,
      checkSDG,
      selectedSDGs,
      getSDG,
      activeIndex,
      goals,
      checkSelected,
      selectedGoals,
      removeSDG,
      selectedKpi,
      selectedKpiId,
      spliceSDG,
      learnMoreModal
    }
  },
  components: { Modal }
}
</script>

<style scoped></style>
